import PropTypes from "prop-types";
import Dispatcher from "Dispatcher";
import invariant from "invariant";
import StereotypesActions from "./StereotypesActions";
import { ReduceStore } from "flux/utils";
import LoadObject from "./LoadObject";
import { hotLoadObject } from "./storeUtils";
import userStore from "data/userStore";
import DataManager from "./DataManager";
import EntityName from "data/util/EntityName";
import { objectBuilder } from "jsmodel/compareRecords";
import StereotypeProjection from "data/util/StereotypeProjection";

export const getDefaultKey = (state) => state.defaultKey.getRequiredValue();

export const getDefault = (state) => getStereotypeByKey(state, getDefaultKey(state));

export const getMagicRoot = (state, forEntity = null) => {
    const all = getAllStereotypes(state);
    const predicate = stereotype =>
        stereotype.magicRoot === true;
    if (!forEntity) return all.find(predicate);
    let en;
    if (typeof forEntity === "string") {
        en = new EntityName(forEntity);
    } else if (forEntity instanceof EntityName) {
        en = forEntity;
    } else {
        throw new Error(`Unsupported '${forEntity}' forEntity value; use an EntityName or a three-part entity key`);
    }
    return all.filter(predicate).find(stereotype =>
        stereotype.creatables.some(creatable =>
            creatable.versions.some(version =>
                version.key.indexOf(en.implKey) === 0)));
};

export const getStereotypeByKey = (state, key) => {
    if (!state.byKey.hasOwnProperty(key)) {
        if (!state.defaultKey || !state.defaultKey.hasValue()) {
            throw new Error(`RACE? Let global stereotypes load before requesting '${key}'.`);
        }
        throw new Error(`No '${key}' stereotype is known.`);
    }
    const lo = state.byKey[key];
    if (!lo.hasValue()) {
        throw new Error(`RACE? The '${key}' stereotype is loading...`);
    }
    const s = lo.getValueEnforcing();
    if (s == null) {
        throw new Error(`No '${key}' stereotype is known.`);
    }
    return s;
};

export const getAllStereotypes = (state) => {
    const result = [];
    Object.entries(state.byKey).forEach(([key, lo]) =>
        lo.hasValue() && result.push(lo.getRequiredValue()));
    return result;
};

export const getDataEntryStereotypes = (state) => getAllStereotypes(state)
        .filter(stereotype =>
            stereotype.magicRoot || stereotype.magicRootKey != null);

export const getMergeStereotypes = (state) => getAllStereotypes(state)
        .filter(stereotype =>
            stereotype.magicRoot);

export const getLookupStereotypes = (state) => getAllStereotypes(state)
        .filter(stereotype =>
            !stereotype.magicRoot && stereotype.magicRootKey == null && stereotype.creatables.length);

export const getDataQualityStereotypes = (state) => getAllStereotypes(state)
        .filter(stereotype =>
            stereotype.magicRootKey != null);

export const getFieldType = (stereoInfo, fieldKey) =>
    // todo: index this instead of scanning
    stereoInfo.fields
        .find(it => it.key === fieldKey)
        .type
        .toUpperCase(); // for reasons that are unclear, stereotype field types were lowercased into model.js...

export const getOrderingComponents = stereoInfo =>
    (stereoInfo.orderingComponents || []).map(c => ({
        ...c,
        type: getFieldType(stereoInfo, c.key),
    }));

export const getRecordComparator = stereoInfo => {
    const components = getOrderingComponents(stereoInfo);
    const comparator = objectBuilder(components);
    return (a, b) => comparator(
        new StereotypeProjection(stereoInfo, a),
        new StereotypeProjection(stereoInfo, b),
    );
};

export const buildStereotypeFields = (state, stereotypeKey) => {
    const stereotype = getStereotypeByKey(state, stereotypeKey);
    if(stereotype) {
        return stereotype.fields.map((field) => {
            const newField = {...field, stereotypeKey};
            if (field.otherStereotype && (field.type === "to-one" || field.type === "to-many")) {
                const relStereotype = getStereotypeByKey(state, field.otherStereotype);
                if (typeof relStereotype === "object") {
                    /*
                      not sure where this renaming came from but it seems like if
                      the meta author named the relationship something we would
                      not want to override that? Keeping the override for now.
                    */
                    newField.name = relStereotype.name;
                    // recursive: could cause infinite loop if circular relationship exists.
                    // Not sure if this situation exists:
                    // User -> Group ... Group -> User
                    newField.fields = buildStereotypeFields(state, relStereotype.key);
                }
            }
            return newField;
        });
    }
    return false;
};

let id_seq = 0;

const prepStereotypeInfo = s => {
    const scripts = s.scripts || {};
    if (scripts.hasOwnProperty("toString")) {
        // eslint-disable-next-line no-new-func
        scripts.toString = new Function("instance", scripts.toString);
    }
    if (scripts.hasOwnProperty("toDetailString")) {
        // eslint-disable-next-line no-new-func
        scripts.toDetailString = new Function("instance", scripts.toDetailString);
    }
    return ({
        ...s,
        fields: s.fields.map(field => {
            const processedField = {
                ...field,
                id: "sfi" + ++id_seq,
            };
            if (field.expectedValues && field.expectedValues.length > 0) {
                processedField.hasExpectedValues = true;
                processedField.expectedValueLookup = field.expectedValues
                    .reduce((l, v) => ({
                        ...l,
                        [v.value]: v.label,
                    }), {});
            }
            return processedField;
        }),
        synthetic: EntityName.is(s.key),
        scripts,
        // todo: make an index to avoid scanning
        implementations: (s.implementations || []).map(it => {
            const en = new EntityName(it.key);
            return {
                ...it,
                implementationKey: en.implKey,
                versionKey: en.versionKey,
                // todo: make an index to avoid scanning
                fieldImpls: it.fieldImpls.map(fi => ({
                    ...fi,
                    script: fi.script != null
                        // eslint-disable-next-line no-new-func
                        ? new Function(
                            "instance",
                            "return (" + fi.script + ");",
                        )
                        : () => null,
                })),
            };
        }),
    });
};

const fieldShape = {
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    otherStereotype: PropTypes.string,
    type: PropTypes.string.isRequired,
};
fieldShape.value = PropTypes.arrayOf(PropTypes.shape(fieldShape));
const stereoShape = {
    creatables: PropTypes.arrayOf(PropTypes.shape({
        defaultKey: PropTypes.string,
        label: PropTypes.string.isRequired,
        orgs: PropTypes.arrayOf(PropTypes.string),
        versions: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })).isRequired,
    })).isRequired,
    defaultFieldKey: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape(fieldShape)).isRequired,
    key: PropTypes.string.isRequired,
    magicRoot: PropTypes.bool.isRequired,
    magicRootKey: PropTypes.string,
    magicChildKey: PropTypes.string,
    name: PropTypes.string.isRequired,
    pluralName: PropTypes.string.isRequired,
    supportsEnablement: PropTypes.bool.isRequired,
    scripts: PropTypes.shape({
        toString: PropTypes.func.isRequired,
        toDetailString: PropTypes.func,
    }).isRequired,
    implementations: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        implementationKey: PropTypes.string.isRequired,
        implementationName: PropTypes.string.isRequired,
        versionKey: PropTypes.string.isRequired,
        versionName: PropTypes.string.isRequired,
        entityName: PropTypes.string.isRequired,
        childKey: PropTypes.string,
        creatable: PropTypes.bool,
        fieldImpls: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            script: PropTypes.func.isRequired,
        })),
    })),
    synthetic: PropTypes.bool.isRequired,
    orderingComponents: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        ascending: PropTypes.bool.isRequired,
    })),
};

class StereotypesStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {
            defaultKey: LoadObject.empty(), // LoadObject<String>
            byKey: {}, // Map<String, LoadObject<Stereotype>>
        };
    }

    reduce(state, action) {
        switch (action.type) {
            case StereotypesActions.START_LOAD:
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                ]);
                return {
                    ...state,
                    defaultKey: state.defaultKey.loading(
                        DataManager.loadStereotypes(
                            userStore.getActiveOrgKey()
                        )
                    )
                };
            case StereotypesActions.LOADED:
                state = {
                    ...state,
                    defaultKey: state.defaultKey
                        .receiveValue(action.data.defaultKey),
                    byKey: { // so we can mutate safely
                        ...state.byKey,
                    },
                };
                action.data.stereotypes
                    .map(prepStereotypeInfo)
                    .forEach(s => {
                        state.byKey[s.key] = (state.byKey[s.key] || LoadObject.empty()
                            .withPropTypes(stereoShape))
                            .receiveValue(s);
                    });
                return state;
            case StereotypesActions.LOAD_ERROR:
                return {
                    defaultKey: state.defaultKey.receiveError(action.error),
                };

            case StereotypesActions.START_STEREOTYPE_LOAD: {
                invariant(
                    state.defaultKey && state.defaultKey.hasValue(),
                    "Request for single stereotype before request for all?",
                );
                return {
                    ...state,
                    byKey: {
                        ...state.byKey,
                        [action.key]: (state.byKey[action.key] || LoadObject.empty()
                            .withPropTypes(stereoShape))
                            .loading(DataManager.loadStereotype(action.key)),
                    },
                };
            }

            case StereotypesActions.STEREOTYPE_LOADED:
                return {
                    ...state,
                    byKey: {
                        ...state.byKey,
                        [action.key]: (state.byKey[action.key] || LoadObject.empty()
                            .withPropTypes(stereoShape))
                            .receiveValue(prepStereotypeInfo(action.data)),
                    },
                };
            case StereotypesActions.STEREOTYPE_LOAD_ERROR:
                return {
                    ...state,
                    byKey: {
                        ...state.byKey,
                        [action.key]: (state.byKey[action.key] || LoadObject.empty()
                            .withPropTypes(stereoShape))
                            .receiveError(action.error),
                    },
                };
            default:
                return state;
        }
    }

    getDefaultKey() {
        return getDefaultKey(this.getState());
    }

    getDefault() {
        return getDefault(this.getState());
    }

    getMagicRoot(forEntity = null) {
        return getMagicRoot(this.getState(), forEntity);
    }

    getStereotypeByKey(key) {
        return getStereotypeByKey(this.getState(), key);
    }

    getStereotypesByKeys(keys) {
        const s = this.getState();
        return keys.map(k => getStereotypeByKey(s, k));
    }

    getStereotypeFieldsByKey(key) {
        return buildStereotypeFields(this.getState(), key);
    }

    getStereotypeLO(key) {
        return hotLoadObject(
            () => this.getState().byKey[key],
            () => Dispatcher.dispatch({
                type: StereotypesActions.START_STEREOTYPE_LOAD,
                key,
            })
        );
    }

    getAllStereotypesLO() {
        return hotLoadObject(
            () => this.getState().defaultKey,
            () => Dispatcher.dispatch({
                type: StereotypesActions.START_LOAD,
            })
        );
    }

    getAllStereotypes() {
        return getAllStereotypes(this.getState());
    }

    getDataEntryStereotypes() {
        return getDataEntryStereotypes(this.getState());
    }

    getMergeStereotypes() {
        return getMergeStereotypes(this.getState());
    }

    getLookupStereotypes() {
        return getLookupStereotypes(this.getState());
    }

    getDataQualityStereotypes() {
        return getDataQualityStereotypes(this.getState());
    }

    getImplementations(key) {
        return this.getStereotypeByKey(key).implementations;
    }

}

export default new StereotypesStore();



// WEBPACK FOOTER //
// ./app/data/StereotypesStore.js